import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Grid, useTheme, Collapse, Link, Box, Typography, TextField, Chip } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import TuneIcon from '@mui/icons-material/Tune'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import {
  generateFilters,
  updateSelectedFilters,
  getSelectedFiltersFromQueryString,
} from './helpers'
import { filterDefinitions, UserType, filterGroups } from './filterDefinitions'
import { useIdentity } from 'pages/Identity/Login'
import { useStyles } from './styles'
import { SavedFiltersButton } from './SavedFiltersButton'
import AlertsButton from './AlertsButton'
import { FilterHeader } from './FilterHeader'
import { MobileFilterDropdown } from './FilterDropdown/MobileFilterDropdown'
import { DesktopFilterDropdown } from './FilterDropdown/DesktopFilterDropdown'
import DesktopCompositeFilterDropdown from './FilterDropdown/DesktopCompositeFilterDropdown'
import { fetchUserFilterAlerts, fetchFilterTypes } from '../../redux/filters'
import PhysicianSignUpDialog from '../../components/Dialog/PhysicianSignUpDialog'
import SaveFilterDialog from '../Dialog/SaveFilterDialog'
import MobileCompositeFilterDropdown from './FilterDropdown/MobileCompositeFilterDropdown'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'

const formatCurrency = (value) => {
  if (!value || value <= 1) return null
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

export const AuthenticatedFilters = ({
  filterType,
  filterValues,
  listType,
  applyFilters,
  disabled,
  resultCount,
  favoritesOnly,
  onToggleFavorites,
  isAIMode,
  onAiChipDelete,
}) => {
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('md'))
  const styles = useStyles({ mobile })
  const { userFilterAlerts = [], filterTypes } = useSelector(state => state.filters)
  const { signedIn } = useSelector(state => state.identity)
  const { jobListingArguments } = useSelector(state => state.aiJobSearch)
  let ref = useRef(null)
  const { employerId, isEmployerAdmin, userId, employerUserId, candidateId } = useIdentity()
  const userType = employerId || isEmployerAdmin ? UserType.EMPLOYER : UserType.CANDIDATE
  const [searchParams, setSearchParams] = useSearchParams()

  const [selectedFilters, setSelectedFilters] = useState({})
  const [showAllFilters, setShowAllFilters] = useState(false)
  const [isFilterSectionOpen, setIsFilterSectionOpen] = useState(false)
  const filters = generateFilters(filterType, userType, filterValues)
  const dispatch = useDispatch()
  const userFilterAlertParam = searchParams.get('userFilterAlertId')
  const [openSaveDialog, setOpenSaveDialog] = useState(false)
  const [openMoreDialog, setOpenMoreDialog] = useState(false)
  const [openSaveFilterDialog, setOpenSaveFilterDialog] = useState(false)
  const isInitialMountRef = useRef(true)

  useEffect(() => {
    dispatch(fetchFilterTypes())
  }, [])

  // Ensure More Filters section is collapsed on mobile
  useEffect(() => {
    if (mobile) {
      setShowAllFilters(false)
    }
  }, [mobile])

  useEffect(() => {
    const filtersFromQuery = getSelectedFiltersFromQueryString(searchParams, filterValues)
    setSelectedFilters(prevFilters =>
      JSON.stringify(prevFilters) !== JSON.stringify(filtersFromQuery)
        ? filtersFromQuery
        : prevFilters,
    )
  }, [filterValues, searchParams])

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }

    applyFilters({ searchParams, employerId, employerUserId, candidateId, isEmployerAdmin })
    // Only auto-expand More Filters on desktop, not mobile
    if (!mobile) {
      Object.keys(selectedFilters).forEach(key => {
        const filterDef = filterDefinitions.find(filter => filter.name === key)
        if (filterDef && filterDef.showMoreFilters) {
          setShowAllFilters(true)
          return true
        }
      })
    }
  }, [selectedFilters, mobile])

  useEffect(() => {
    dispatch(fetchUserFilterAlerts(userId))
  }, [userId])

  const prevAlertParamRef = useRef(userFilterAlertParam)

  useEffect(() => {
    if (prevAlertParamRef.current !== userFilterAlertParam) {
      prevAlertParamRef.current = userFilterAlertParam
      applyFilters({ searchParams, employerId, employerUserId, candidateId, isEmployerAdmin })
    }
  }, [userFilterAlertParam, employerId, employerUserId, candidateId, isEmployerAdmin])

  const userFilterAlertTypeName = listType === 'job' ? 'job' : 'candidate'
  const availableAlerts =
    userFilterAlerts.filter(
      alert =>
        alert.filterType.toLowerCase() === userFilterAlertTypeName && Number(alert.recordCount) > 0,
    ) || []

  const handleSaveDialogClose = () => {
    setOpenSaveDialog(false)
  }

  const handleSaveDialogOpen = () => {
    dispatch(
      fetchTrackEvent({
        eventName: 'save_filter_lead_click',
        eventType: ProductAnalyticsEventTypes.BUTTON_CLICK,
      }),
    )
    setOpenSaveDialog(true)
  }

  const handleMoreDialogClose = () => setOpenMoreDialog(false)
  const handleMoreDialogOpen = () => {
    dispatch(
      fetchTrackEvent({
        eventName: 'more_filters_lead_click',
        eventType: ProductAnalyticsEventTypes.BUTTON_CLICK,
      }),
    )
    setOpenMoreDialog(true)
  }

  const handleSaveFilterDialogClose = () => setOpenSaveFilterDialog(false)
  const handleSaveFilterDialogOpen = () => setOpenSaveFilterDialog(true)

  // Calculate count of selected More Filters
  const getMoreFiltersCount = () => {
    let count = 0
    Object.keys(selectedFilters).forEach(key => {
      const filterDef = filterDefinitions.find(filter => filter.name === key)
      if (filterDef && filterDef.showMoreFilters && selectedFilters[key]?.selectedValues?.length > 0) {
        count += selectedFilters[key].selectedValues.length
      }
    })
    return count
  }

  const moreFiltersCount = getMoreFiltersCount()

  const handleFilterChange = selectedFilter => {
    if (disabled) return
    const { filterDefinition, selectedValues } = selectedFilter

    const newSearchParams = filterDefinition.clearOthers
      ? new URLSearchParams()
      : new URLSearchParams(searchParams)

    if (selectedValues.length > 0) {
      const valueString = filterDefinition.isYesNo
        ? selectedValues[0].value === '1'
        : selectedValues.map(x => x.value).join('|')

      newSearchParams.set(filterDefinition.queryStringKey, valueString)
    } else {
      newSearchParams.delete(filterDefinition.queryStringKey)
    }

    setSearchParams(newSearchParams)

    setSelectedFilters(
      selectedValues.length > 0 ? updateSelectedFilters(selectedFilters, selectedFilter) : {},
    )
  }

  const handleClearAll = () => {
    if (disabled) return
    const newSearchParams = new URLSearchParams(searchParams)
    filterDefinitions.forEach(filterDefinition => {
      if (filterDefinition.clearAll === true) {
        newSearchParams.delete(filterDefinition.queryStringKey)
      }
    })

    setSearchParams(newSearchParams)
    setSelectedFilters(currentFilters => {
      const updatedFilters = { ...currentFilters }
      Object.keys(updatedFilters).forEach(key => {
        if (filterDefinitions.find(filter => filter.name === key)) {
          delete updatedFilters[key]
        }
      })
      return updatedFilters
    })
  }

  const toggleShowAllFilters = () => {
    setShowAllFilters(!showAllFilters)
  }

  const toggleFilterSection = () => {
    setIsFilterSectionOpen(!isFilterSectionOpen)
  }

  const handleSaveLinkClick = event => {
    if (!signedIn) {
      event.preventDefault()
      handleSaveDialogOpen()
    } else {
      handleSaveFilterDialogOpen()
    }
  }

  const handleMoreLinkClick = event => {
    if (!signedIn) {
      event.preventDefault()
      handleMoreDialogOpen()
    } else {
      toggleShowAllFilters()
    }
  }

  const handleGroupFilterChange = ({ selectedValues, filterDefinition }) => {
    if (disabled) return
    const newSearchParams = new URLSearchParams(searchParams)
    if (selectedValues.length > 0) {
      const valueString = filterDefinition.isYesNo
        ? selectedValues[0].value === '1'
        : selectedValues.map(x => x.value).join('|')
      if (valueString) {
        newSearchParams.set(filterDefinition.queryStringKey, valueString)
      } else {
        newSearchParams.delete(filterDefinition.queryStringKey)
      }
    } else {
      newSearchParams.delete(filterDefinition.queryStringKey)
    }

    setSearchParams(newSearchParams)
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterDefinition.name]: {
        filterDefinition,
        selectedValues,
      },
    }))
  }

  // Build compact chip bar chips from active filters
  const compactChips = useMemo(() => {
    const chips = []

    Object.entries(selectedFilters).forEach(([filterName, filterData]) => {
      const { filterDefinition, selectedValues } = filterData
      if (!selectedValues || selectedValues.length === 0) return

      if (filterDefinition.isYesNo) {
        chips.push({
          key: filterName,
          label: filterDefinition.displayName,
          onDelete: () => {
            if (disabled) return
            const newParams = new URLSearchParams(searchParams)
            newParams.delete(filterDefinition.queryStringKey)
            setSearchParams(newParams)
          },
        })
      } else if (filterName === 'candidateTypes') {
        const name = selectedValues[0]?.name
        if (name) {
          chips.push({
            key: 'candidateTypes',
            label: name,
            onDelete: () => {
              if (disabled) return
              const newParams = new URLSearchParams(searchParams)
              newParams.delete('candidateTypeId')
              newParams.delete('specialtyIds')
              setSearchParams(newParams)
            },
          })
        }
      } else {
        selectedValues.forEach((value, idx) => {
          chips.push({
            key: `${filterName}-${value.value}`,
            label: value.name,
            onDelete: () => {
              if (disabled) return
              const remaining = selectedValues.filter((_, i) => i !== idx)
              handleFilterChange({ filterDefinition, selectedValues: remaining })
            },
          })
        })
      }
    })

    return chips
  }, [selectedFilters, searchParams, disabled])

  // Build AI-specific salary chips
  const aiSalaryChips = useMemo(() => {
    if (!isAIMode || !jobListingArguments) return []
    const chips = []

    if (jobListingArguments.salaryMinimum) {
      chips.push({
        key: 'salary-min',
        label: `Min ${formatCurrency(jobListingArguments.salaryMinimum)}`,
        onDelete: onAiChipDelete ? () => onAiChipDelete('salaryMin', null) : undefined,
      })
    }

    if (jobListingArguments.salaryDesired) {
      chips.push({
        key: 'salary-desired',
        label: `Target ${formatCurrency(jobListingArguments.salaryDesired)}`,
      })
    }

    return chips
  }, [isAIMode, jobListingArguments, onAiChipDelete])

  const hasActiveChips = compactChips.length > 0 || aiSalaryChips.length > 0

  const chipBarHeight = '28px'
  const chipBarFontSize = '0.8rem'

  const renderFilters = filtersToRender => {
    const validFilters = filtersToRender.filter(
      filter => filter.filterValues && filter.filterValues.length > 0,
    )

    const { groupedFilters, ungroupedFilters } = validFilters.reduce(
      (acc, filter) => {
        if (filter.filterDefinition?.filterGroup) {
          const displayName = filterGroups.find(
            filterGroup => filterGroup.name === filter.filterDefinition.filterGroup,
          ).displayName
          const existingGroup = acc.groupedFilters.find(
            group => group.filterGroup === filter.filterDefinition?.filterGroup,
          )

          if (existingGroup) {
            existingGroup.filters.push(filter)
          } else {
            acc.groupedFilters.push({
              filterGroup: filter.filterDefinition.filterGroup,
              displayName: displayName,
              filters: [filter],
            })
          }
        } else {
          acc.ungroupedFilters.push(filter)
        }
        return acc
      },
      { groupedFilters: [], ungroupedFilters: [] },
    )

    const sortedFilters = [
      ...ungroupedFilters.filter(filter => !filter.filterDefinition.isYesNo),
      ...ungroupedFilters.filter(filter => filter.filterDefinition.isYesNo),
    ]

    return (
      <>
        <Grid container spacing={mobile ? 1 : 2} sx={{
          px: mobile ? 2 : 0,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }}>
          {sortedFilters.map((filter, index) => {
            const selectedFilter = selectedFilters[filter.filterDefinition.name]
            const FilterComponent = mobile ? MobileFilterDropdown : DesktopFilterDropdown

            return (
              <Grid
                item
                key={`${filter.filterDefinition.name}-${index}`}
                xs={12}
                sm={mobile ? 12 : 6}
                md={2}
                sx={{
                  display: 'flex',
                  justifyContent: mobile ? 'stretch' : 'flex-start',
                  paddingTop: '8px',
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                <FilterComponent
                  filterDefinition={filter.filterDefinition}
                  options={filter.filterValues}
                  checked={selectedFilter ? selectedFilter.selectedValues : null}
                  onChange={handleFilterChange}
                  styles={styles}
                  mobile={mobile}
                />
              </Grid>
            )
          })}

          <Grid item xs={12}
                sm={mobile ? 12 : 6}
                md={2}
                sx={{
                  display: 'flex',
                  justifyContent: mobile ? 'stretch' : 'flex-start',
                  paddingTop: '8px',
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}>
            {groupedFilters.map(groupedFilter => {
              const CompositeFilterComponent = mobile
                ? MobileCompositeFilterDropdown
                : DesktopCompositeFilterDropdown
              return (
                <CompositeFilterComponent
                  groupedFilter={groupedFilter}
                  onChange={handleGroupFilterChange}
                  selectedFilters={selectedFilters}
                  styles={styles}
                />
              )
            })}
          </Grid>
        </Grid>
      </>
    )
  }

  return (
    <>
      <Grid
        container
        ref={ref}
        justifyContent='center'
        sx={{
          overflow: 'hidden',
          padding: 0,
          position: 'relative',
          zIndex: 5,
          mt: mobile ? '56px' : 0,
          ...(disabled && {
            pointerEvents: 'none',
            opacity: 0.5,
          }),
        }}
      >
        {/* Collapsible Filter Panel — expands above the chip bar */}
        <Collapse in={isFilterSectionOpen} sx={{ width: '100%' }}>
          <Grid
            container
            className={`filterWrapper ${styles.filterHeaderWrapper}`}
            sx={{
              overflow: 'hidden',
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            <Grid
              container
              item
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{
                width: '100%',
                overflow: 'hidden',
                px: { xs: 2, md: 4 }
              }}
            >
              <FilterHeader
                listType={listType}
                filterValues={filterValues}
                handleFilterChange={handleFilterChange}
                toggleShowAllFilters={toggleShowAllFilters}
                selectedFilters={selectedFilters}
                filterTypes={filterTypes}
                showAllFilters={showAllFilters}
                mobile={mobile}
                isFilterSectionOpen={isFilterSectionOpen}
                toggleFilterSection={null}
              />
            </Grid>
          </Grid>
          <Grid
            container
            className={`filterWrapper ${styles.filterWrapper}`}
            sx={{
              padding: mobile ? '16px' : '10px 0px',
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start'
            }}
          >
            <Grid
              container
              item
              alignItems='center'
              justifyContent='space-between'
              spacing={mobile ? 0 : 2}
              mb={1}
              sx={{
                width: '100%',
                overflow: 'hidden',
                padding: '0px',
                px: { xs: 1, md: 4 }
              }}
            >
              <Grid item xs={mobile ? 12 : 'auto'} sm='auto'>
                <Box sx={{
                  display: 'flex',
                  flexDirection: mobile ? 'column' : 'row',
                  alignItems: 'center',
                  gap: mobile ? '8px' : '12px',
                  mb: mobile ? 1 : 0
                }}>
                  <SavedFiltersButton
                    listType={listType}
                    filterTypes={filterTypes}
                  />
                  <TextField
                    onClick={handleMoreLinkClick}
                    size='small'
                    value={showAllFilters
                      ? 'Hide Filters'
                      : moreFiltersCount > 0
                        ? `More Filters (${moreFiltersCount})`
                        : 'More Filters'
                    }
                    InputProps={{
                      readOnly: true,
                      style: {
                        color: '#2A2A2A',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                      },
                      endAdornment: (
                        <ExpandMoreIcon
                          sx={{
                            fontSize: '20px',
                            color: '#2A2A2A',
                            transform: showAllFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease',
                          }}
                        />
                      ),
                    }}
                    sx={{
                      width: mobile ? '100%' : '200px',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#2A2A2A' },
                        '&:hover fieldset': { borderColor: '#2A2A2A' },
                        '&.Mui-focused fieldset': { borderColor: '#2A2A2A' },
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                      },
                      '& .MuiInputBase-input': {
                        color: '#2A2A2A',
                        fontSize: '0.95rem',
                      },
                    }}
                  />
                </Box>
              </Grid>

              {availableAlerts.length > 0 && userFilterAlertParam !== null && (
                <Grid item xs={mobile ? 12 : 6} sm={mobile ? 12 : 6} md={6}>
                  <AlertsButton availableAlerts={availableAlerts} />
                </Grid>
              )}

              <Grid item xs={mobile ? 12 : 'auto'} sm='auto'>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: mobile ? '12px' : '20px',
                    justifyContent: 'center',
                  }}
                >
                  <Link
                    href='#'
                    onClick={handleClearAll}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#2A2A2A',
                      textDecoration: 'none !important',
                      fontSize: mobile ? '0.9rem' : '1.1rem',
                      whiteSpace: 'nowrap',
                      background: 'none !important',
                      backgroundSize: '0 !important',
                      gap: '5px',
                      '&:hover': {
                        textDecoration: 'none !important',
                        background: 'none !important',
                        backgroundSize: '0 !important',
                      },
                      '&:focus': {
                        textDecoration: 'none !important',
                        background: 'none !important',
                        backgroundSize: '0 !important',
                      },
                    }}
                  >
                    <ClearIcon
                      sx={{
                        fontSize: mobile ? '16px' : '18px',
                        color: '#2A2A2A',
                      }}
                    />
                    Clear Filters
                  </Link>
                  <Link
                    href='#'
                    onClick={handleSaveLinkClick}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#2A2A2A',
                      textDecoration: 'none !important',
                      fontSize: mobile ? '0.9rem' : '1.1rem',
                      whiteSpace: 'nowrap',
                      background: 'none !important',
                      backgroundSize: '0 !important',
                      '&:hover': {
                        textDecoration: 'none !important',
                        background: 'none !important',
                        backgroundSize: '0 !important',
                      },
                      '&:focus': {
                        textDecoration: 'none !important',
                        background: 'none !important',
                        backgroundSize: '0 !important',
                      },
                    }}
                  >
                    Save Filters
                    <SaveIcon
                      sx={{
                        marginLeft: '5px',
                        fontSize: mobile ? '16px' : '18px',
                        color: '#2A2A2A',
                      }}
                    />
                  </Link>
                </Box>
              </Grid>
            </Grid>
            <Grid
              item
              container
              direction='row'
              alignItems='center'
              spacing={0}
              sx={{
                width: '100%',
                overflow: 'hidden',
                px: { xs: 2, md: 4 }
              }}
            >
              <Collapse in={showAllFilters} sx={{ width: '100%' }}>
                <Grid item container direction='row' alignItems='center' spacing={0}>
                  {renderFilters(filters)}
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
        </Collapse>

        {/* Compact Chip Bar — always visible */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.75,
            px: { xs: 1.5, md: 4 },
            py: 0.75,
            backgroundColor: theme.palette.grey[50],
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Filters toggle */}
          <Chip
            icon={<TuneIcon sx={{ fontSize: '0.95rem !important' }} />}
            label={isFilterSectionOpen ? 'Hide Filters' : 'Filters'}
            size="small"
            onClick={toggleFilterSection}
            sx={{
              height: chipBarHeight,
              fontSize: chipBarFontSize,
              fontWeight: 600,
              cursor: 'pointer',
              ...(isFilterSectionOpen
                ? {
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    '& .MuiChip-icon': { color: 'white' },
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                  }
                : {
                    backgroundColor: 'white',
                    border: `1px solid ${theme.palette.grey[300]}`,
                    '&:hover': { backgroundColor: theme.palette.grey[100] },
                  }
              ),
            }}
          />

          {/* Active filter chips */}
          {compactChips.map(chip => (
            <Chip
              key={chip.key}
              label={chip.label}
              size="small"
              onDelete={chip.onDelete}
              sx={{
                height: chipBarHeight,
                fontSize: chipBarFontSize,
                backgroundColor: 'white',
                border: `1px solid ${theme.palette.grey[300]}`,
                '& .MuiChip-deleteIcon': {
                  fontSize: '0.9rem',
                  color: theme.palette.grey[500],
                  '&:hover': { color: theme.palette.grey[700] },
                },
              }}
            />
          ))}

          {/* AI salary chips */}
          {aiSalaryChips.map(chip => (
            <Chip
              key={chip.key}
              label={chip.label}
              size="small"
              icon={<AttachMoneyIcon />}
              onDelete={chip.onDelete}
              sx={{
                height: chipBarHeight,
                fontSize: chipBarFontSize,
                backgroundColor: theme.palette.success.light,
                border: `1px solid ${theme.palette.success.main}`,
                '& .MuiChip-icon': { fontSize: '0.9rem' },
                '& .MuiChip-deleteIcon': {
                  fontSize: '0.9rem',
                  color: theme.palette.success.dark,
                  '&:hover': { color: theme.palette.success.main },
                },
              }}
            />
          ))}

          {/* Clear all */}
          {hasActiveChips && (
            <Chip
              label="Clear All"
              size="small"
              onClick={handleClearAll}
              sx={{
                height: chipBarHeight,
                fontSize: chipBarFontSize,
                cursor: 'pointer',
                backgroundColor: 'transparent',
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.grey[200],
                  color: theme.palette.text.primary,
                },
              }}
            />
          )}

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Results count */}
          {resultCount !== undefined && (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ whiteSpace: 'nowrap', fontSize: chipBarFontSize }}
            >
              {isAIMode
                ? `${resultCount} job${resultCount !== 1 ? 's' : ''}`
                : `${resultCount ? resultCount.toLocaleString() : 0} jobs`
              }
            </Typography>
          )}

          {/* Favorites toggle */}
          {onToggleFavorites && (
            <Chip
              icon={favoritesOnly
                ? <FavoriteIcon sx={{ fontSize: '0.9rem !important', color: `${theme.palette.error.main} !important` }} />
                : <FavoriteBorderIcon sx={{ fontSize: '0.9rem !important' }} />
              }
              label="Favorites"
              size="small"
              onClick={onToggleFavorites}
              sx={{
                height: chipBarHeight,
                fontSize: chipBarFontSize,
                cursor: 'pointer',
                ...(favoritesOnly
                  ? {
                      backgroundColor: theme.palette.error.light,
                      color: theme.palette.error.dark,
                      border: `1px solid ${theme.palette.error.main}`,
                      fontWeight: 600,
                      '&:hover': { backgroundColor: theme.palette.error.light },
                    }
                  : {
                      backgroundColor: 'white',
                      border: `1px solid ${theme.palette.grey[300]}`,
                      '&:hover': { backgroundColor: theme.palette.grey[100] },
                    }
                ),
              }}
            />
          )}
        </Box>
      </Grid>

      <PhysicianSignUpDialog
        openDialog={openSaveDialog}
        handleDialogClose={handleSaveDialogClose}
        feature='save'
      />
      <PhysicianSignUpDialog
        openDialog={openMoreDialog}
        handleDialogClose={handleMoreDialogClose}
        feature='more'
      />
      <SaveFilterDialog
        openDialog={openSaveFilterDialog}
        handleDialogClose={handleSaveFilterDialogClose}
        selectedFilters={selectedFilters}
        filterTypes={filterTypes}
        listType={listType}
      />
    </>
  )
}
